import React from 'react';
import moment from 'moment';
import { TTimesheetBooking } from './types';
import { isValidNumber, createNumberInputHandler, createTextInputHandler } from './utils';
import { CardHeader } from './shared/CardHeader';
import { EditableField } from './shared/EditableField';

interface BookingCardProps {
    booking: TTimesheetBooking;
    isEditing: boolean;
    onUpdateBooking: (
        bookingId: number,
        updater: (booking: TTimesheetBooking) => TTimesheetBooking,
    ) => void;
}

export const BookingCard: React.FC<BookingCardProps> = React.memo(({
    booking,
    isEditing,
    onUpdateBooking,
}) => {
    const formattedDate = booking.date
        ? moment(booking.date).format('DD/MM/YYYY')
        : '-';

    const handleDurationChange = createNumberInputHandler(
        booking.id,
        'duration',
        onUpdateBooking,
    );

    const handleFreelancerRateChange = createNumberInputHandler(
        booking.id,
        'freelancerPayRate',
        onUpdateBooking,
    );

    const handleClientPayChange = createNumberInputHandler(
        booking.id,
        'clientPayRate',
        onUpdateBooking,
    );

    const handleRateCodeChange = createTextInputHandler(
        booking.id,
        'rateCode',
        onUpdateBooking,
    );

    const handleRateDescriptionChange = createTextInputHandler(
        booking.id,
        'rateDescription',
        onUpdateBooking,
    );

    const hasDurationError = !isValidNumber(booking.duration);
    const hasFreelancerRateError = !isValidNumber(booking.freelancerPayRate);
    const hasClientPayError = !isValidNumber(booking.clientPayRate);
    const hasRateCodeError = !booking.rateCode?.trim();
    const hasRateDescriptionError = !booking.rateDescription?.trim();

    return (
        <div className="timesheet-booking-card">
            <CardHeader
                pills={[
                    { label: 'Job Ref', value: booking.jobReference },
                    { label: 'Booking Ref', value: booking.reference_number },
                    { label: 'Date', value: formattedDate },
                ]}
            />
            <div className="timesheet-booking-card__body">
                <table className="timesheet-booking-table">
                    <tbody>
                        <EditableField
                            label="Duration (hrs)"
                            value={booking.duration}
                            isEditing={isEditing}
                            hasError={hasDurationError}
                            fieldType="number"
                            onChange={handleDurationChange}
                            ariaLabel={`Duration for booking ${booking.reference_number}`}
                        />
                        <EditableField
                            label="Hourly Rate"
                            value={booking.freelancerPayRate}
                            isEditing={isEditing}
                            hasError={hasFreelancerRateError}
                            fieldType="currency"
                            onChange={handleFreelancerRateChange}
                            ariaLabel={`Hourly rate for booking ${booking.reference_number}`}
                        />
                        <EditableField
                            label="Client Rate"
                            value={booking.clientPayRate}
                            isEditing={isEditing}
                            hasError={hasClientPayError}
                            fieldType="currency"
                            onChange={handleClientPayChange}
                            ariaLabel={`Client pay for booking ${booking.reference_number}`}
                        />
                        <EditableField
                            label="Rate Code"
                            value={booking.rateCode}
                            isEditing={isEditing}
                            hasError={hasRateCodeError}
                            fieldType="text"
                            onChange={handleRateCodeChange}
                            ariaLabel={`Rate code for booking ${booking.reference_number}`}
                        />
                        <EditableField
                            label="Description"
                            value={booking.rateDescription}
                            isEditing={isEditing}
                            hasError={hasRateDescriptionError}
                            fieldType="textarea"
                            onChange={handleRateDescriptionChange}
                            ariaLabel={`Description for booking ${booking.reference_number}`}
                        />
                    </tbody>
                </table>
            </div>
        </div>
    );
});

BookingCard.displayName = 'BookingCard';

